import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import Footer from "~/components/Footer";
import styles from "~/styles/guide.module.css";

export const meta: MetaFunction = () => [
  { title: "User Guide — PageBurn" },
  { name: "description", content: "Everything you need to read without resistance. From first steps to advanced configuration." },
];

export default function PageBurnGuidePage() {
  return (
    <div className={styles.pbTheme}>

      <section className={styles.hero}>
        <p className={styles.heroLabel}>Official Documentation</p>
        <h1 className={styles.heroTitle}>USER<br />GUIDE.</h1>
        <p className={styles.heroSub}>
          Everything you need to read without resistance.
          From first steps to advanced configuration.
        </p>
      </section>

      <nav className={styles.toc} aria-label="Table of contents">
        {[
          { num: "01", label: "What is PageBurn?", href: "#what" },
          { num: "02", label: "Getting started", href: "#start" },
          { num: "03", label: "Settings", href: "#config" },
          { num: "04", label: "Customization", href: "#custom" },
          { num: "05", label: "PRO Version", href: "#pro" },
          { num: "06", label: "Tips", href: "#tips" },
        ].map(({ num, label, href }) => (
          <a key={href} href={href} className={styles.tocItem}>
            <span className={styles.tocNum}>{num}</span>
            <span className={styles.tocLabel}>{label}</span>
          </a>
        ))}
      </nav>

      <div className="rule" />

      <div className={styles.guideContent}>

        <section id="what" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>01</p>
          <h2>What is PageBurn?</h2>
          <p>
            PageBurn is a variable-speed reader that uses the RSVP (Rapid Serial Visual
            Presentation) technique. Instead of moving your eyes across a page, the words
            come to you — one at a time, at the center of the screen.
          </p>
          <p>The result: you read faster, with less effort and without losing the thread.</p>
          <p>
            You can read anything: an article, a PDF, a book chapter, a poem, a long
            email. If it has text, PageBurn can read it for you.
          </p>
          <div className={styles.tipBox}>
            <p className={styles.tipBoxLabel}>Why does it work?</p>
            <p>
              Most of the time we spend &ldquo;reading&rdquo; is spent moving our eyes
              and processing the page layout. RSVP eliminates that overhead — your brain
              receives the words directly, without visual friction.
            </p>
          </div>
        </section>

        <section id="start" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>02</p>
          <h2>Getting started</h2>
          <h3><span className={styles.stepNum}>STEP 1</span> Load a text</h3>
          <p>Tap the <strong>T</strong> button to open the text panel. You can paste any text directly, choose from the free ebook library, or upload a file (PDF, TXT, MD — PRO required for uploads).</p>
          <h3><span className={styles.stepNum}>STEP 2</span> Play</h3>
          <p>Press the <strong>▶</strong> button to start reading. Words will appear one by one at the center of the screen. Press again to pause at any time.</p>
          <h3><span className={styles.stepNum}>STEP 3</span> Adjust speed</h3>
          <p>Open the <strong>⚙</strong> settings panel and use the WPM slider to find your comfortable pace. Start slow — you can always increase later.</p>
          <div className={styles.wpmScale}>
            {[
              { label: "150 WPM", pct: 21, desc: "Ideal to start" },
              { label: "300 WPM", pct: 43, desc: "Medium, comfortable" },
              { label: "500 WPM", pct: 71, desc: "Advanced" },
              { label: "700+ WPM", pct: 100, desc: "Expert" },
            ].map(({ label, pct, desc }) => (
              <div key={label} className={styles.wpmItem}>
                <span className={styles.wpmLabel}>{label}</span>
                <div className={styles.wpmBarWrap}>
                  <div className={styles.wpmBar} style={{ width: `${pct}%` }} />
                </div>
                <span className={styles.wpmDesc}>{desc}</span>
              </div>
            ))}
          </div>
        </section>

        <section id="config" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>03</p>
          <h2>Settings</h2>
          <p>All settings are accessible from the <strong>⚙</strong> panel while a text is loaded. Changes apply in real time.</p>
          <ul>
            <li><strong>WPM</strong> — Reading speed from 100 to 700+ words per minute. The default is 250 WPM.</li>
            <li><strong>Font size</strong> — Four sizes: Small, Medium, Large, XL.</li>
            <li><strong>Color theme</strong> — Light, Dark, or Sepia. All three are designed to minimize eye fatigue.</li>
            <li><strong>Chunk size</strong> — Display 1 word at a time (default) or 2–3 words per flash (PRO feature).</li>
          </ul>
        </section>

        <section id="custom" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>04</p>
          <h2>Customization</h2>
          <p>PageBurn is designed to disappear — your environment should serve you, not compete for attention.</p>
          <ul>
            <li><strong>Background color</strong> — Adjust to match your ambient light.</li>
            <li><strong>Font weight</strong> — Thin to Bold.</li>
            <li><strong>RSVP highlight</strong> — The colored central letter (ORP) can be toggled on or off.</li>
            <li><strong>PRO themes</strong> — Unlock four exclusive skins: Dark Pro, Paper, Ink, and Amber.</li>
          </ul>
        </section>

        <section id="pro" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>05</p>
          <h2>PRO Version</h2>
          <p>PageBurn PRO is a one-time lifetime purchase. No subscription, no renewal — pay once and own it forever.</p>
          <ul>
            <li>Everything included in Free</li>
            <li>PDF, TXT, and Markdown (.md) file upload</li>
            <li>Library synced across up to 3 devices</li>
            <li>All skins and themes (Dark Pro, Paper, Ink, Amber)</li>
            <li>First year of new feature updates included</li>
            <li>Priority access to upcoming features</li>
            <li>2–3 word chunk reading mode</li>
          </ul>
          <h3><span className={styles.stepNum}>HOW TO ACTIVATE</span> Two steps</h3>
          <p>
            Go to{" "}
            <a href="https://pageburn.site" target="_blank" rel="noopener noreferrer">pageburn.site</a>
            {" "}and tap the <strong>PRO</strong> button in the top bar. After payment, your PRO status activates immediately.
          </p>
          <div className={styles.tipBox}>
            <p className={styles.tipBoxLabel}>Early adopter pricing</p>
            <p>PRO Lifetime is currently $12.90 — a launch-phase price that will rise to $27.90 in Phase 2. Once you purchase, your price is locked in forever.</p>
          </div>
        </section>

        <section id="tips" className={styles.guideSection}>
          <p className={styles.guideSectionLabel}>06</p>
          <h2>Tips for better RSVP reading</h2>
          <ul>
            <li><strong>Start at 200–250 WPM</strong> — Even fast readers benefit from starting slow.</li>
            <li><strong>Use breaks</strong> — PageBurn reminds you to rest your eyes every 20 minutes.</li>
            <li><strong>Read actively</strong> — Pause and reflect after sections.</li>
            <li><strong>Use the highlight</strong> — The orange center letter (ORP) trains your focus to land in the right spot every flash.</li>
            <li><strong>Match speed to content</strong> — Adaptive Speed handles this automatically.</li>
          </ul>
        </section>

      </div>

      <div className="rule" />

      <div className={styles.ctaBlock}>
        <h2 className={styles.ctaTitle}>Ready to <span>start</span>?</h2>
        <a href="https://pageburn.site" target="_blank" rel="noopener noreferrer" className="btn-primary">
          Open PageBurn
        </a>
        <Link to="/pageburn" className={styles.ctaBack}>← PageBurn Manifesto</Link>
      </div>

      <div className="rule" />
      <Footer />
    </div>
  );
}
